<script setup>
import CommissionNote from '~/components/marketing/CommissionNote.vue';
import MarketingCtaBtn from '~/components/marketing/MarketingCtaBtn.vue';
import DownloadAppBanner from '~/components/design/DownloadAppBanner.vue';
import FeatureRow from '~/components/features/FeatureRow.vue';
import FeatureDialog from '~/components/features/FeatureDialog.vue';
import FeatureIconOrbit from '~/components/features/FeatureIconOrbit.vue';
import FeatureLimitsGraphic from '~/components/features/FeatureLimitsGraphic.vue';
import {
  COMMISSION_PERCENT, FEATURE_FAQS, FEATURE_ITEMS, FEATURE_SECTIONS, findFeature,
} from '~/utils/features';

// Every feature, for a club admin deciding whether this will take work off
// their plate. Sections are the admin's jobs, rows are what stops being their
// problem: one feature per line, so the whole platform fits a few screens. A
// row opens its explainer (?feature=key, shareable) or, for the formats, the
// format's own page.
definePageMeta({
  name: 'Features',
  path: '/features',
});

const {setMeta} = useHeadService();
const {accountCtaLabel} = useMarketingCta();
const route = useRoute();
const router = useRouter();

setMeta(
    'Every feature, no limits | Tap Leagues',
    `Box leagues, ladders, tournaments, americano leagues and club nights with RSVP. Payments into the club's own account, handicaps, live scoring, chat and a clubhouse TV. Unlimited members and competitions, free, with ${COMMISSION_PERCENT}% plus Stripe fees only on payments taken through Tap Leagues.`,
);

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Tap Leagues',
        url: 'https://tapleagues.com/features',
        applicationCategory: 'SportsApplication',
        operatingSystem: 'Web, iOS, Android',
        offers: {'@type': 'Offer', price: '0', priceCurrency: 'GBP'},
        featureList: FEATURE_ITEMS.filter(item => item.tag !== 'soon').map(item => item.title),
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FEATURE_FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {'@type': 'Answer', text: faq.a},
        })),
      }),
    },
  ],
});

// The open feature lives in the URL, so a card can be linked to and the back
// button behaves. Replace, never push, so browsing cards does not pile up history.
// The dialog is a teleported overlay: it only opens once the page has mounted,
// never during server render or hydration (that 500s the page).
const mounted = ref(false);
onMounted(() => {
  mounted.value = true;
});
const selected = computed(() => findFeature(route.query.feature));
const dialogOpen = computed({
  get: () => mounted.value && selected.value != null,
  set: (value) => {
    if (!value && route.query.feature) {
      router.replace({query: {...route.query, feature: undefined}});
    }
  },
});
const openFeature = (item) => router.replace({query: {...route.query, feature: item.key}});

const sectionId = (section) => `features-${section.key}`;
</script>

<template>
  <base-page>
    <!-- ===== HERO ===== -->
    <section class="ft-hero">
      <div class="ft-hero-glow" aria-hidden="true"/>
      <v-container class="ft-wrap ft-hero-inner">
        <div class="ft-hero-grid">
          <div class="ft-hero-copy">
            <span class="ft-eyebrow">Every feature, every club</span>
            <h1 class="ft-title">Every feature of Tap Leagues</h1>
            <p class="ft-lead">
              Box leagues, ladders, tournaments, americano leagues and club nights, with payments, handicaps,
              live scoring and chat built in. All of it on every club, free*, with no limits on members or
              competitions.
            </p>
            <p class="ft-note"><commission-note/></p>
          </div>
          <div class="ft-hero-art">
            <feature-icon-orbit/>
          </div>
        </div>
      </v-container>
    </section>

    <v-container class="ft-wrap">
      <!-- ===== NO LIMITS ===== -->
      <section class="ft-limits" aria-labelledby="features-limits-title">
        <div class="ft-limits-copy">
          <span class="ft-eyebrow">Limits</span>
          <h2 id="features-limits-title" class="ft-h2">No limits</h2>
          <p class="ft-sub">
            Nothing to outgrow. Unlimited members, admins, competitions, divisions and seasons on every club.
            The only cost is {{ COMMISSION_PERCENT }}% plus Stripe's fees on payments your club takes through
            Tap Leagues, and only if you take them.
          </p>
        </div>
        <feature-limits-graphic/>
      </section>

      <!-- ===== SECTIONS ===== -->
      <section
          v-for="section in FEATURE_SECTIONS"
          :id="sectionId(section)"
          :key="section.key"
          class="ft-section"
          :aria-labelledby="`${sectionId(section)}-title`">
        <div class="ft-section-head">
          <span class="ft-section-icon"><v-icon size="22" :icon="section.icon"/></span>
          <div>
            <h2 :id="`${sectionId(section)}-title`" class="ft-h2">{{ section.title }}</h2>
            <p class="ft-sub">{{ section.lead }}</p>
          </div>
        </div>
        <div class="ft-table tl-card" role="list">
          <feature-row v-for="item in section.items" :key="item.key" :item="item" role="listitem" @open="openFeature"/>
        </div>
      </section>

      <!-- ===== FAQ ===== -->
      <section class="ft-section" aria-labelledby="features-faq-title">
        <div class="ft-section-head">
          <span class="ft-section-icon"><v-icon size="22" icon="mdi-help-circle-outline"/></span>
          <div>
            <h2 id="features-faq-title" class="ft-h2">Questions</h2>
          </div>
        </div>
        <div class="ft-faqs">
          <div v-for="faq in FEATURE_FAQS" :key="faq.q" class="ft-faq tl-card">
            <h3>{{ faq.q }}</h3>
            <p>{{ faq.a }}</p>
          </div>
        </div>
      </section>

      <!-- ===== CTA ===== -->
      <section class="ft-final">
        <div class="ft-final-streaks" aria-hidden="true"/>
        <div class="ft-final-inner">
          <h2 class="ft-final-title">Set up your club</h2>
          <p class="ft-final-sub">About ten minutes to a club and a first competition. No card needed.</p>
          <div class="ft-final-cta">
            <marketing-cta-btn color="white" size="large" class="text-primary">{{ accountCtaLabel }}</marketing-cta-btn>
            <v-btn variant="outlined" color="white" size="large" to="/contact">Talk to us</v-btn>
          </div>
        </div>
      </section>

      <div class="ft-app">
        <download-app-banner/>
      </div>
    </v-container>

    <feature-dialog v-model="dialogOpen" :feature="selected"/>
  </base-page>
</template>

<style scoped lang="scss">
.ft-wrap {
  max-width: 1100px;
}

/* ---- hero ---- */
.ft-hero {
  position: relative;
  overflow: hidden;
  background:
      radial-gradient(circle at 14% 0%, rgba(211, 41, 70, 0.08), transparent 46%),
      linear-gradient(180deg, rgb(var(--v-theme-surface)) 0%, rgb(var(--v-theme-light)) 100%);
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.ft-hero-inner {
  padding-top: 36px;
  padding-bottom: 36px;

  @media (min-width: 960px) {
    padding-top: 48px;
    padding-bottom: 48px;
  }
}

.ft-eyebrow {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 12px;
}

.ft-title {
  font-size: clamp(30px, 5vw, 46px);
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1.05;
  max-width: 760px;
}

.ft-lead {
  font-size: 16px;
  line-height: 1.7;
  color: rgba(var(--v-theme-charcoal), 0.72);
  max-width: 720px;
  margin-top: 14px;
}

.ft-note {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 8px;
}

/* Copy on the left, the orbit on the right; stacked on a phone with the orbit beneath */
.ft-hero-grid {
  display: grid;
  gap: 28px;
  align-items: center;

  @media (min-width: 960px) {
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
    gap: 40px;
  }
}

.ft-hero-art {
  display: flex;
  justify-content: center;
}

/* ---- shared section rhythm ---- */
.ft-h2 {
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.15;
}

.ft-sub {
  font-size: 15px;
  line-height: 1.65;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 8px;
  max-width: 640px;
}

.ft-section {
  margin-top: 44px;
  scroll-margin-top: 80px;

  @media (min-width: 960px) {
    margin-top: 56px;
  }
}

.ft-section-head {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.ft-section-icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-brandTint));
  margin-top: 2px;
}

.ft-table {
  margin-top: 18px;
  overflow: hidden;
}

/* ---- limits ---- */
.ft-limits {
  margin-top: 48px;
  display: grid;
  gap: 24px;

  @media (min-width: 960px) {
    margin-top: 72px;
    grid-template-columns: 1fr 1.2fr;
    align-items: center;
    gap: 40px;
  }
}

/* ---- FAQ: open text, same data as the FAQPage JSON-LD ---- */
.ft-faqs {
  display: grid;
  gap: 14px;
  margin-top: 22px;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.ft-faq {
  padding: 18px;

  h3 {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }

  p {
    font-size: 13.5px;
    line-height: 1.65;
    color: rgb(var(--v-theme-charcoal));
    margin-top: 6px;
  }
}

/* ---- final CTA band ---- */
.ft-final {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin-top: 72px;
  padding: 56px 24px;
  text-align: center;
  color: #fff;
  background: linear-gradient(135deg, #E0314F 0%, #D32946 44%, #A31C35 100%);
  box-shadow: 0 30px 60px -30px rgba(163, 28, 53, 0.75);

  @media (min-width: 960px) {
    margin-top: 100px;
    padding: 72px 40px;
  }
}

.ft-final-streaks {
  position: absolute;
  inset: 0;
  z-index: 0;
  background: repeating-linear-gradient(118deg, transparent 0 38px, rgba(255, 255, 255, 0.05) 38px 40px);
}

.ft-final-inner {
  position: relative;
  z-index: 1;
}

.ft-final-title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.8px;

  @media (min-width: 960px) {
    font-size: 36px;
  }
}

.ft-final-sub {
  font-size: 14px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 10px;
}

.ft-final-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 26px;
}

.ft-app {
  margin-top: 40px;
  margin-bottom: 24px;
}
</style>
